<template>
  <v-navigation-drawer
    :value="navigationDrawerOpen"
    :mini-variant="navigationDrawerMini"
    :mobile-breakpoint="960"
    app
    clipped
    permanent
    dark
    color="secondary"
    class="app-navigation-drawer app-navigation-drawer--dark"
    :class="{ 'app-navigation-drawer--mini': navigationDrawerMini }"
    :width="navigationDrawerWidth"
    :mini-variant-width="navigationDrawerMiniWidth"
    @input="$emit('update:navigationDrawerOpen', $event)"
  >
    <div
      class="app-navigation-drawer__inner app-navigation-drawer__layout"
      :aria-expanded="!navigationDrawerMini ? 'true' : 'false'"
    >
      <div
        class="app-navigation-drawer__toggle-row d-flex align-center flex-shrink-0"
        :class="{ 'app-navigation-drawer__toggle-row--pinned': !navigationDrawerMini }"
      >
        <div class="app-navigation-drawer__toggle-spacer" aria-hidden="true" />
        <v-btn
          icon
          small
          class="app-navigation-drawer__toggle-btn"
          :aria-label="toggleAriaLabel"
          @click="$emit('toggle-navigation-drawer')"
        >
          <v-icon small color="white">mdi-dock-right</v-icon>
        </v-btn>
      </div>

      <div class="app-navigation-drawer__scroll">
        <navigation-drawer-nav-panel
          v-if="!navigationDrawerMini"
          :is-admin="isAdmin"
          :saved-searches-drawer-open="savedSearchesDrawerOpen"
          @toggle-saved-searches-drawer="$emit('toggle-saved-searches-drawer')"
          @close-saved-searches-drawer="$emit('close-saved-searches-drawer')"
        />
      </div>

      <div
        class="app-navigation-drawer__footer flex-shrink-0"
        :class="{ 'app-navigation-drawer__footer--mini': navigationDrawerMini }"
      >
        <v-card
          v-if="showSetupCard && !navigationDrawerMini"
          flat
          light
          elevation="0"
          class="intelligence-setup-banner app-navigation-drawer__setup-card app-navigation-drawer__gutter mt-3 mb-3 pa-3"
          @click="$emit('continue-setup')"
        >
          <div class="text-caption font-weight-medium mb-2 banner-title">Intelligence Setup</div>
          <v-progress-linear
            :value="setupProgress"
            color="primary"
            height="6"
            rounded
            class="progress-bar"
            background-color="#e5e7eb"
          />
          <div class="text-caption mt-1 progress-text">
            {{ setupProgress }}% complete
          </div>
        </v-card>

        <v-list
          dense
          class="py-0"
          :class="{ 'app-navigation-drawer__gutter pb-3': !navigationDrawerMini }"
        >
          <navigation-drawer-nav-item
            v-for="item in visibleFooterNav"
            :key="item.id"
            :item="item"
            :active="isFooterItemActive(item)"
            :mini="navigationDrawerMini"
            variant="footer"
            @click="onFooterClick(item)"
          />
        </v-list>
      </div>
    </div>
  </v-navigation-drawer>
</template>

<script>
import { footerNav, filterNavItems, isNavItemActive } from '@/config/appNavigation.js';
import NavigationDrawerNavItem from './NavigationDrawerNavItem.vue';
import NavigationDrawerNavPanel from './NavigationDrawerNavPanel.vue';
import {
  NAVIGATION_DRAWER_WIDTH,
  NAVIGATION_DRAWER_MINI_WIDTH,
} from './navigationDrawerLayout.js';

export default {
  name: 'AppNavigationDrawer',

  components: {
    NavigationDrawerNavItem,
    NavigationDrawerNavPanel,
  },

  props: {
    navigationDrawerOpen: {
      type: Boolean,
      default: true,
    },
    navigationDrawerMini: {
      type: Boolean,
      default: false,
    },
    isAdmin: {
      type: Boolean,
      default: false,
    },
    setupProgress: {
      type: Number,
      default: 0,
    },
    showSetupCard: {
      type: Boolean,
      default: true,
    },
    savedSearchesDrawerOpen: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      navigationDrawerWidth: NAVIGATION_DRAWER_WIDTH,
      navigationDrawerMiniWidth: NAVIGATION_DRAWER_MINI_WIDTH,
    };
  },

  computed: {
    toggleAriaLabel() {
      return this.navigationDrawerMini ? 'Expand navigation drawer' : 'Collapse navigation drawer';
    },
    visibleFooterNav() {
      return filterNavItems(footerNav);
    },
  },

  methods: {
    onFooterClick(item) {
      if (item.action === 'openUtilityDrawer' && item.drawerTab != null) {
        this.$emit('open-utility-drawer', item.drawerTab);
      }
    },
    isFooterItemActive(item) {
      return isNavItemActive(item, this.$route);
    },
  },
};
</script>

<style scoped>
.app-navigation-drawer {
  border-right: 1px solid rgba(255, 255, 255, 0.08);
}

.app-navigation-drawer >>> .v-navigation-drawer__content {
  background: #12253a !important;
  text-align: left;
  height: 100% !important;
  overflow: hidden !important;
  display: flex;
  flex-direction: column;
}

.app-navigation-drawer.v-navigation-drawer--mini-variant >>> .v-navigation-drawer__content {
  align-items: flex-start;
}

.app-navigation-drawer__layout {
  display: grid;
  grid-template-rows: auto 1fr auto;
  width: 100%;
  height: 100%;
  min-height: 0;
  overflow: hidden;
}

.app-navigation-drawer__scroll {
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
}

/* Panel footer must not show in scroll (lives in .app-navigation-drawer__footer only) */
.app-navigation-drawer__scroll >>> .navigation-drawer-nav-panel__footer {
  display: none !important;
}

.app-navigation-drawer__toggle-row {
  position: relative;
  z-index: 1;
  flex-shrink: 0;
  min-height: 40px;
  width: 100%;
  padding: 4px 0 0 8px;
  justify-content: flex-start !important;
  background: #12253a;
  transition: padding 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.app-navigation-drawer__toggle-row--pinned {
  padding: 4px 6px 0 0;
}

.app-navigation-drawer__toggle-spacer {
  flex: 0 1 0%;
  min-width: 0;
  transition: flex-grow 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.app-navigation-drawer__toggle-row--pinned .app-navigation-drawer__toggle-spacer {
  flex-grow: 1;
}

.app-navigation-drawer--dark >>> .v-list-item__title {
  color: rgba(255, 255, 255, 0.9) !important;
}

.app-navigation-drawer__toggle-btn {
  flex-shrink: 0;
  margin-left: 0 !important;
}

.app-navigation-drawer__footer {
  background: #12253a;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  z-index: 2;
}

.app-navigation-drawer__footer--mini {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-bottom: 12px;
}

.app-navigation-drawer__footer--mini >>> .v-list {
  width: 100%;
}

.app-navigation-drawer__gutter {
  padding-left: 8px;
  padding-right: 8px;
}

.app-navigation-drawer__setup-card {
  cursor: pointer;
}

.app-navigation-drawer__setup-card:hover {
  border-color: #f57c00 !important;
}
</style>
